import { createAsyncThunk } from "@reduxjs/toolkit";

import { ACTIVITY_STATE } from "Constants/constants";
import logger from "Libs/logger";
import { isJson } from "Libs/utils";
import { AsyncThunkOptionType } from "Reducers/types";
import { getEnvironmentActivitiesRequest } from "Api/activity/getEnvironmentActivities.request";

import { getActivityTypes } from "../activity.slice";

import type { Activity } from "Api/activity/types";

export interface LoadEnvironmentPendingScheduledActivities {
  environmentDescriptionId: string;
  projectDescriptionId: string;
  organizationDescriptionId: string;
  filterNames: string[];
}

// Without a count, the API returns its default page (~10 items, newest
// first) *before* the state filter is meaningful protection: since any
// activity can now be scheduled (not just maintenance ones), an
// environment with 10+ more-recently-created scheduled activities of
// unrelated types (crons, tasks, rescheduled redeploys) could still push
// the one we actually need off that page. This count is a generous
// ceiling on how many activities can realistically be scheduled (not yet
// run) at once.
const PENDING_SCHEDULED_ACTIVITIES_COUNT = 100;

export const loadEnvironmentPendingScheduledActivities = createAsyncThunk<
  {
    activities: Activity[];
    organizationDescriptionId: string;
    environmentDescriptionId: string;
    projectDescriptionId: string;
  },
  LoadEnvironmentPendingScheduledActivities,
  AsyncThunkOptionType
>(
  "app/activity/environment/pendingScheduled/load",
  async (
    {
      projectDescriptionId,
      environmentDescriptionId,
      organizationDescriptionId,
      filterNames
    },
    { rejectWithValue }
  ) => {
    // Not hardcoded to maintenance types: any activity can be scheduled.
    // But it must still respect the caller's activity type filter (the
    // same one loadEnvironmentActivities uses), or a deselected type
    // would keep showing up in Pending regardless of the filter. Matches
    // loadEnvironmentActivities: don't request anything when no filter is
    // selected.
    const filters = getActivityTypes(filterNames);
    if (!filters.length) {
      return {
        activities: [],
        organizationDescriptionId,
        environmentDescriptionId,
        projectDescriptionId
      };
    }

    try {
      const encodedEnvId = encodeURIComponent(environmentDescriptionId);
      const activities = await getEnvironmentActivitiesRequest({
        projectId: projectDescriptionId,
        encodedEnvId,
        filters,
        state: [ACTIVITY_STATE.SCHEDULED],
        count: PENDING_SCHEDULED_ACTIVITIES_COUNT
      });

      return {
        activities,
        organizationDescriptionId,
        environmentDescriptionId,
        projectDescriptionId
      };
    } catch (err: unknown) {
      const status = (err as { code?: number })?.code;
      if (!status || ![404, 403].includes(status)) {
        const errorMessage = isJson(err)
          ? err
          : "An error occurred while attempting to load environment pending scheduled activities.";
        logger(errorMessage, {
          action: "environment_load_pending_scheduled_activities",
          meta: {
            organizationDescriptionId,
            environmentDescriptionId,
            projectDescriptionId
          }
        });
      }

      return rejectWithValue(err);
    }
  }
);
